import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginWithGoogle, getMe, logout as apiLogout } from '../api/auth';
import { getRefreshToken } from '../api/client';

const AuthContext = createContext(null);

// Mirrors the capability rules in apps/authentication/models.py so the
// client can recompute access locally instead of relying on the values
// the backend baked into the response at fetch time. Without this every
// consumer would keep showing reading/writing as allowed until the page
// is reloaded or refreshUser() is called — which is exactly the bug
// where students could keep using the app after their promo expired.
function deriveLiveAccess(user) {
  if (!user) return user;
  const rawPlan = String(user.plan || 'free').toLowerCase();
  const expiresAtMs = user.plan_expires_at ? new Date(user.plan_expires_at).getTime() : null;
  // Trust the backend's effective_plan — it's recomputed on every
  // /auth/me request from the canonical server clock. We only override
  // it locally when (a) the backend already said 'promo' and (b) the
  // expiry time has crossed since the page was loaded — i.e. a real
  // mid-session expiry the server hasn't yet caught up to. Otherwise an
  // admin-set plan='promo' with no/stale plan_expires_at would
  // wrongly flip the UI to 'free' even though the backend reports
  // 'promo' for capability checks.
  const backendEffective = String(user.effective_plan || rawPlan).toLowerCase();
  const expiredMidSession = backendEffective === 'promo'
    && expiresAtMs !== null
    && expiresAtMs <= Date.now();
  const effectivePlan = expiredMidSession ? 'free' : backendEffective;
  const credits = Number(user.ai_credits || 0);
  const planAllowsReading = ['promo', 'basic', 'ai'].includes(effectivePlan);
  const planAllowsSpeaking = ['promo', 'ai'].includes(effectivePlan);
  const liveCapabilities = {
    reading: planAllowsReading,
    writing: planAllowsReading || credits > 0,
    speaking: planAllowsSpeaking && credits > 0,
    full_exam: planAllowsSpeaking && credits > 0,
    ai_marking: credits > 0,
    buy_credits: rawPlan === 'ai',
  };
  return {
    ...user,
    effective_plan: effectivePlan,
    plan_expired: expiredMidSession,
    capabilities: liveCapabilities,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumps once when the user's promo trial crosses its expiry — only
  // signal we need to recompute deriveLiveAccess() outside of regular
  // user-object updates. Previously this provider polled every 15s and
  // pushed a new context value to *every* useAuth() consumer (the
  // dashboard's 50+ rows, the profile menu, every reading screen…) on
  // every tick. That was the dominant cause of the dashboard feeling
  // sluggish.
  const [expiryEpoch, setExpiryEpoch] = useState(0);

  const refreshUser = useCallback(async () => {
    const me = await getMe();
    setUser(me);
    return me;
  }, []);

  useEffect(() => {
    const restore = async () => {
      const refresh = getRefreshToken();
      if (!refresh) { setLoading(false); return; }
      try {
        await refreshUser();
      } catch {
        /* the user simply isn't logged in; surface as null */
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, [refreshUser]);

  // Re-pull /auth/me when the tab becomes visible or window regains
  // focus, so admin / DB edits to the user (plan changes, credit
  // grants, manual resets) show up the moment a user comes back to the
  // app — instead of forcing them to hard-refresh. Throttled to once
  // per 30s so quickly tabbing between windows doesn't hammer the API.
  useEffect(() => {
    if (!user) return undefined;
    let lastRefresh = Date.now();
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefresh < 30_000) return;
      lastRefresh = Date.now();
      refreshUser().catch(() => { /* network blip — try again on next focus */ });
    };
    document.addEventListener('visibilitychange', maybeRefresh);
    window.addEventListener('focus', maybeRefresh);
    return () => {
      document.removeEventListener('visibilitychange', maybeRefresh);
      window.removeEventListener('focus', maybeRefresh);
    };
  }, [user, refreshUser]);

  // Schedule a single setTimeout to fire exactly when the user's promo
  // trial expires (with a 1s buffer to avoid races with the server
  // clock). Free users and already-expired users don't get a timer at
  // all — no interval, no re-renders, no work. This replaces the old
  // 15s polling tick.
  useEffect(() => {
    if (!user?.plan_expires_at) return undefined;
    const expiresAtMs = new Date(user.plan_expires_at).getTime();
    const msUntilExpiry = expiresAtMs - Date.now();
    if (msUntilExpiry <= 0) {
      // Already expired by the time we got here — bump once so consumers
      // see the locked state, then pull /auth/me to sync server-side too.
      setExpiryEpoch((e) => e + 1);
      refreshUser().catch(() => { /* keep the local gate; retry on next nav */ });
      return undefined;
    }
    const handle = setTimeout(() => {
      setExpiryEpoch((e) => e + 1);
      refreshUser().catch(() => { /* local gate already flipped, server will catch up next request */ });
    }, msUntilExpiry + 1000);
    return () => clearTimeout(handle);
  }, [user?.plan_expires_at, refreshUser]);

  const login = useCallback(async (credential) => {
    const userData = await loginWithGoogle(credential);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  // Memoize liveUser so a fresh object only appears when the raw user
  // changes or the promo expiry flips. Reference stability is what lets
  // memoized children skip re-rendering.
  const liveUser = useMemo(() => deriveLiveAccess(user), [user, expiryEpoch]);

  const value = useMemo(
    () => ({ user: liveUser, loading, login, logout, refreshUser }),
    [liveUser, loading, login, logout, refreshUser],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
